import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from '../axiosConfig';
import DeliveryMethodSelector from '../components/checkout/DeliveryMethodSelector';
import CustomerFacingSubtotal from '../components/checkout/CustomerFacingSubtotal';

const InvoicePage = () => {
  const params = useParams();
  const orderId = params.orderId || params.invoiceRef;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [requiresEmail, setRequiresEmail] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');

  // Delivery address state
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [deliveryZonesLoading, setDeliveryZonesLoading] = useState(false);
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [needsDeliveryAddress, setNeedsDeliveryAddress] = useState(false); // Blocks payment until delivery is set
  const [shippingInfo, setShippingInfo] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: ''
  });

  // Chowdeck delivery method state
  const [deliveryMethod, setDeliveryMethod] = useState('STATIC');
  const [chowdeckFeeId, setChowdeckFeeId] = useState(null);
  const [chowdeckDeliveryFee, setChowdeckDeliveryFee] = useState(null);

  // Adire-inspired SVG pattern for decorative borders
  const adirePattern = `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231e3a5f' fill-opacity='0.15'%3E%3Cpath d='M20 0L0 20h8l12-12 12 12h8L20 0zm0 40L0 20h8l12 12 12-12h8L20 40z'/%3E%3Ccircle cx='20' cy='20' r='4'/%3E%3Ccircle cx='0' cy='0' r='2'/%3E%3Ccircle cx='40' cy='0' r='2'/%3E%3Ccircle cx='0' cy='40' r='2'/%3E%3Ccircle cx='40' cy='40' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

  useEffect(() => {
    const fetchInvoice = async () => {
      if (!token) {
        setError('Invalid invoice link. Missing authentication token.');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`/invoices/${orderId}?token=${token}`);
        if (response.data.success) {
          const invoiceData = response.data.invoice;
          setInvoice(invoiceData);

          // Set default selected gateway
          if (invoiceData.enabledGateways?.length > 0) {
            setSelectedGateway(invoiceData.enabledGateways[0].name);
          }

          // Pre-fill customer info if available
          if (invoiceData.customer) {
            const names = (invoiceData.customer.name || '').split(' ');
            setShippingInfo(prev => ({
              ...prev,
              firstName: names[0] || '',
              lastName: names.slice(1).join(' ') || '',
              email: invoiceData.customer.email || '',
              phone: invoiceData.customer.phone || ''
            }));
          }

          // Check if delivery fee needs to be calculated
          // If shippingFee/deliveryFee is 0 on an unpaid order, we need the customer to confirm/select delivery
          // Even if they have an address, the fee wasn't calculated so they need to go through delivery selection
          const shippingFee = parseFloat(invoiceData.shippingFee) || 0;
          const deliveryFee = parseFloat(invoiceData.deliveryFee) || 0;
          const hasDeliveryFee = shippingFee > 0 || deliveryFee > 0;

          const isUnpaid = invoiceData.status !== 'PAID';
          // Simple rule: unpaid + no delivery fee = needs delivery selection
          const needsDelivery = isUnpaid && !hasDeliveryFee;

          console.log('[Invoice] Delivery check:', {
            status: invoiceData.status,
            isUnpaid,
            shippingFee,
            deliveryFee,
            hasDeliveryFee,
            needsDelivery
          });

          if (needsDelivery) {
            // Set flag to block payment until delivery is added
            console.log('[Invoice] Setting needsDeliveryAddress = true');
            setNeedsDeliveryAddress(true);
            // Pre-fetch delivery zones
            fetchDeliveryZones();

            // Pre-fill existing address info if available
            const existingAddress = invoiceData.customer?.address;
            if (existingAddress && typeof existingAddress === 'object') {
              setShippingInfo(prev => ({
                ...prev,
                address1: existingAddress.address || existingAddress.address1 || prev.address1,
                city: existingAddress.city || prev.city,
                state: existingAddress.state || prev.state
              }));
            }
          } else {
            console.log('[Invoice] Delivery NOT needed - hasDeliveryFee:', hasDeliveryFee, 'isUnpaid:', isUnpaid);
          }
        } else {
          setError(response.data.error || 'Failed to load invoice');
        }
      } catch (err) {
        console.error('Invoice fetch error:', err);
        setError(err.response?.data?.error || 'Failed to load invoice. Please check the link and try again.');
      } finally {
        setLoading(false);
      }
    };

    const fetchDeliveryZones = async () => {
      try {
        console.log('[Invoice] Fetching delivery zones...');
        setDeliveryZonesLoading(true);
        const response = await axios.get(`/invoices/${orderId}/delivery-zones?token=${token}`);
        console.log('[Invoice] Delivery zones response:', response.data);
        if (response.data.success) {
          console.log('[Invoice] Setting delivery zones:', response.data.deliveryZones);
          setDeliveryZones(response.data.deliveryZones);
        }
      } catch (err) {
        console.error('Failed to fetch delivery zones:', err);
      } finally {
        setDeliveryZonesLoading(false);
      }
    };

    fetchInvoice();
  }, [orderId, token]);

  const handlePayment = async () => {
    if (!selectedGateway) {
      alert('Please select a payment method');
      return;
    }

    // If email is required and not provided, show error
    if (requiresEmail && !customerEmail) {
      alert('Please enter your email address to continue');
      return;
    }

    // Validate email format if provided
    if (customerEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(customerEmail)) {
        alert('Please enter a valid email address');
        return;
      }
    }

    setPaymentLoading(true);
    try {
      const response = await axios.post(`/invoices/${orderId}/pay`, {
        token,
        paymentProvider: selectedGateway,
        customerEmail: customerEmail || undefined
      });

      if (response.data.success) {
        // Redirect to payment gateway
        window.location.href = response.data.payment.authorizationUrl;
      } else {
        alert(response.data.error || 'Failed to initialize payment');
      }
    } catch (err) {
      console.error('Payment initialization error:', err);

      // Check if email is required
      if (err.response?.data?.requiresEmail) {
        setRequiresEmail(true);
        alert('Please enter your email address to continue with payment');
      } else {
        alert(err.response?.data?.error || 'Failed to initialize payment. Please try again.');
      }
    } finally {
      setPaymentLoading(false);
    }
  };

  // Update delivery address and recalculate total
  const handleUpdateDelivery = async () => {
    if (!shippingInfo.state) {
      alert('Please select a delivery state');
      return;
    }

    if (!shippingInfo.address1) {
      alert('Please enter your delivery address');
      return;
    }

    console.log('[Invoice] Submitting delivery update with shippingInfo:', shippingInfo);
    console.log('[Invoice] Available zones when submitting:', deliveryZones);

    setDeliveryLoading(true);
    try {
      const response = await axios.post(`/invoices/${orderId}/update-delivery`, {
        token,
        shippingInfo,
        // Chowdeck delivery method (if selected)
        deliveryMethod: deliveryMethod,
        chowdeckFeeId: deliveryMethod === 'CHOWDECK' ? chowdeckFeeId : null
      });

      if (response.data.success) {
        // Check if merchant doesn't deliver to this state
        if (response.data.noDeliveryZone) {
          alert('Delivery to your state is not currently available. Please select a different state or contact the store.');
          return; // Don't update anything, let user try another state
        }

        // Update invoice with new totals
        setInvoice(prev => ({
          ...prev,
          shippingFee: response.data.deliveryFee,
          deliveryFee: response.data.deliveryFee,
          total: response.data.total,
          customer: {
            ...prev.customer,
            address: shippingInfo
          },
          // Store delivery method info for display
          deliveryMethodUsed: response.data.deliveryMethod,
          chowdeckData: response.data.chowdeck
        }));
        setShowDeliveryForm(false);
        // Delivery address added successfully - allow payment now
        setNeedsDeliveryAddress(false);

        // Show success message for Chowdeck
        if (response.data.deliveryMethod === 'CHOWDECK') {
          console.log('[Invoice] Chowdeck delivery confirmed:', response.data.chowdeck);
        }
      } else {
        alert(response.data.error || 'Failed to update delivery information');
      }
    } catch (err) {
      console.error('Update delivery error:', err);
      alert(err.response?.data?.error || 'Failed to update delivery information');
    } finally {
      setDeliveryLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const currencyCode = invoice?.currency || 'NGN';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Handle delivery method change (Chowdeck integration)
  const handleDeliveryMethodChange = useCallback((data) => {
    console.log('[Invoice] Delivery method changed:', data);
    setDeliveryMethod(data.method);

    if (data.method === 'CHOWDECK') {
      setChowdeckFeeId(data.feeId);
      setChowdeckDeliveryFee(data.fee);
    } else {
      setChowdeckFeeId(null);
      setChowdeckDeliveryFee(null);
    }
  }, []);

  // Get the actual delivery fee to use (Chowdeck or standard)
  const getSelectedDeliveryFee = () => {
    if (deliveryMethod === 'CHOWDECK' && chowdeckDeliveryFee !== null) {
      return chowdeckDeliveryFee;
    }
    // Find standard fee from delivery zones
    const zone = deliveryZones.find(z => z.state === shippingInfo.state);
    return zone?.deliveryFee || 0;
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e5e7eb',
            borderTopColor: '#1e3a5f',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading invoice...</p>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          padding: '32px',
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: '#fef2f2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <svg width="24" height="24" fill="none" stroke="#ef4444" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4m0 4h.01" />
            </svg>
          </div>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#111827' }}>
            Unable to Load Invoice
          </h2>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>{error}</p>
        </div>
      </div>
    );
  }

  const isPaid = invoice.status === 'PAID';

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      padding: '20px'
    }}>
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div style={{
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        {/* Invoice Card */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>
          {/* Adire Pattern Top Border */}
          <div style={{
            height: '16px',
            background: adirePattern,
            backgroundColor: '#f0f4f8',
            borderBottom: '3px solid #1e3a5f'
          }} />

          {/* Header */}
          <div style={{
            padding: '24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {invoice.store?.logo && (
                <img
                  src={invoice.store.logo}
                  alt={invoice.store.name}
                  style={{
                    width: '48px',
                    height: '48px',
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }}
                />
              )}
              <div>
                <h1 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#1e3a5f' }}>
                  {invoice.store?.name || 'Invoice'}
                </h1>
                <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0' }}>
                  Invoice #{invoice.invoiceNumber}
                </p>
              </div>
            </div>
            <div style={{
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              background: isPaid ? '#dcfce7' : '#fef3c7',
              color: isPaid ? '#166534' : '#92400e'
            }}>
              {isPaid ? 'PAID' : 'PENDING'}
            </div>
          </div>

          {/* Invoice Details */}
          <div style={{ padding: '24px' }}>
            {/* Dates and Customer Info */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              marginBottom: '24px',
              fontSize: '13px'
            }}>
              <div>
                <p style={{ color: '#6b7280', margin: '0 0 4px' }}>Issue Date</p>
                <p style={{ color: '#111827', fontWeight: '500', margin: 0 }}>
                  {formatDate(invoice.issuedAt)}
                </p>
              </div>
              {invoice.dueDate && (
                <div>
                  <p style={{ color: '#6b7280', margin: '0 0 4px' }}>Due Date</p>
                  <p style={{ color: '#111827', fontWeight: '500', margin: 0 }}>
                    {formatDate(invoice.dueDate)}
                  </p>
                </div>
              )}
              <div style={{ gridColumn: '1 / -1' }}>
                <p style={{ color: '#6b7280', margin: '0 0 4px' }}>Bill To</p>
                <p style={{ color: '#111827', fontWeight: '500', margin: 0 }}>
                  {invoice.customer?.name || 'Customer'}
                </p>
                {invoice.customer?.email && (
                  <p style={{ color: '#6b7280', margin: '2px 0 0', fontSize: '12px' }}>
                    {invoice.customer.email}
                  </p>
                )}
              </div>
            </div>

            {/* Line Items */}
            <div style={{
              background: '#f9fafb',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <h3 style={{ fontSize: '13px', fontWeight: '600', color: '#1e3a5f', margin: '0 0 12px', textTransform: 'uppercase' }}>
                Items
              </h3>
              {invoice.items?.map((item, index) => (
                <div
                  key={item.id || index}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom: index < invoice.items.length - 1 ? '1px solid #e5e7eb' : 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {item.image && (
                      <img
                        src={item.image}
                        alt={item.name}
                        style={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'cover',
                          borderRadius: '6px'
                        }}
                      />
                    )}
                    <div>
                      <p style={{ margin: 0, fontWeight: '500', color: '#111827', fontSize: '14px' }}>
                        {item.name}
                      </p>
                      <p style={{ margin: '2px 0 0', color: '#6b7280', fontSize: '12px' }}>
                        {formatCurrency(item.price)} x {item.quantity}
                      </p>
                    </div>
                  </div>
                  <p style={{ margin: 0, fontWeight: '600', color: '#111827', fontSize: '14px' }}>
                    {formatCurrency(item.total)}
                  </p>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div style={{ marginBottom: '24px' }}>
              <CustomerFacingSubtotal
                subtotal={invoice.subtotal}
                processingFee={
                  invoice.serviceCharge > 0
                    && String(invoice.serviceChargeWhoPays || 'customer').toLowerCase() === 'customer'
                    ? invoice.serviceCharge
                    : 0
                }
                formatCurrency={formatCurrency}
                compact
                rowStyle={{ marginBottom: '8px', fontSize: '14px' }}
                labelStyle={{ color: '#6b7280', fontWeight: 400 }}
                valueStyle={{ color: '#111827', fontWeight: 400 }}
                subLabel={false}
              />
              {invoice.taxTotal > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                  <span style={{ color: '#6b7280' }}>Tax</span>
                  <span style={{ color: '#111827' }}>{formatCurrency(invoice.taxTotal)}</span>
                </div>
              )}
              {(invoice.shippingFee > 0 || invoice.deliveryFee > 0) ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                  <span style={{ color: invoice.deliveryMethodUsed === 'CHOWDECK' ? '#ea580c' : '#6b7280' }}>
                    {invoice.deliveryMethodUsed === 'CHOWDECK' ? '⚡ Chowdeck Express' : 'Delivery'}
                  </span>
                  <span style={{ color: invoice.deliveryMethodUsed === 'CHOWDECK' ? '#ea580c' : '#111827' }}>
                    {formatCurrency(invoice.shippingFee || invoice.deliveryFee)}
                  </span>
                </div>
              ) : !isPaid && needsDeliveryAddress && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                  <span style={{ color: '#6b7280' }}>Delivery</span>
                  <span style={{ color: '#f59e0b', fontStyle: 'italic', fontSize: '13px' }}>Address required</span>
                </div>
              )}
              {invoice.discountTotal > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                  <span style={{ color: '#6b7280' }}>Discount</span>
                  <span style={{ color: '#22c55e' }}>-{formatCurrency(invoice.discountTotal)}</span>
                </div>
              )}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: '12px',
                borderTop: '2px solid #1e3a5f',
                marginTop: '12px'
              }}>
                <span style={{ fontSize: '16px', fontWeight: '700', color: '#1e3a5f' }}>Total</span>
                <span style={{ fontSize: '16px', fontWeight: '700', color: '#1e3a5f' }}>{formatCurrency(invoice.total)}</span>
              </div>
            </div>

            {/* Delivery Address Section - Show when delivery info is missing */}
            {showDeliveryForm && !isPaid && (
              <div className="no-print" style={{
                background: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '16px'
              }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#92400e', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Delivery Address Required
                </h3>
                <p style={{ fontSize: '13px', color: '#78350f', margin: '0 0 16px' }}>
                  Please provide your delivery address to calculate shipping and complete your order.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>First Name</label>
                    <input
                      type="text"
                      value={shippingInfo.firstName}
                      onChange={(e) => setShippingInfo(prev => ({ ...prev, firstName: e.target.value }))}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Last Name</label>
                    <input
                      type="text"
                      value={shippingInfo.lastName}
                      onChange={(e) => setShippingInfo(prev => ({ ...prev, lastName: e.target.value }))}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Phone Number</label>
                  <input
                    type="tel"
                    value={shippingInfo.phone}
                    onChange={(e) => setShippingInfo(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="e.g., 08012345678"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    State <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select
                    value={shippingInfo.state}
                    onChange={(e) => setShippingInfo(prev => ({ ...prev, state: e.target.value }))}
                    disabled={deliveryZonesLoading}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', backgroundColor: deliveryZonesLoading ? '#f3f4f6' : 'white' }}
                  >
                    {deliveryZonesLoading ? (
                      <option value="">Loading delivery options...</option>
                    ) : deliveryZones.length > 0 ? (
                      <>
                        <option value="">Select your state</option>
                        {deliveryZones.map(zone => (
                          <option key={zone.id} value={zone.state}>
                            {zone.state} - {formatCurrency(zone.deliveryFee)}
                            {zone.estimatedDays && ` (${zone.estimatedDays} days)`}
                          </option>
                        ))}
                      </>
                    ) : (
                      // Fallback - no zones configured by merchant
                      <>
                        <option value="">No delivery zones configured</option>
                        <option value="" disabled style={{ fontSize: '12px', color: '#6b7280' }}>
                          Please contact the store for delivery options
                        </option>
                      </>
                    )}
                  </select>
                  {!deliveryZonesLoading && deliveryZones.length === 0 && (
                    <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                      The merchant has not configured delivery zones. Please contact them directly.
                    </p>
                  )}
                </div>

                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>City</label>
                  <input
                    type="text"
                    value={shippingInfo.city}
                    onChange={(e) => setShippingInfo(prev => ({ ...prev, city: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Address <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={shippingInfo.address1}
                    onChange={(e) => setShippingInfo(prev => ({ ...prev, address1: e.target.value }))}
                    placeholder="Street address"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Chowdeck Delivery Option - Show when state and address are filled */}
                {shippingInfo.state && shippingInfo.address1 && invoice?.store?.slug && (
                  <DeliveryMethodSelector
                    storeSlug={invoice.store.slug}
                    deliveryState={shippingInfo.state}
                    deliveryAddress={shippingInfo.address1}
                    deliveryCity={shippingInfo.city}
                    standardShippingCost={deliveryZones.find(z => z.state === shippingInfo.state)?.deliveryFee || 0}
                    standardShippingMethod="Standard Delivery"
                    standardEstimate={deliveryZones.find(z => z.state === shippingInfo.state)?.estimatedDays ?
                      `${deliveryZones.find(z => z.state === shippingInfo.state).estimatedDays} days` : '3-5 business days'}
                    currency={{ format: formatCurrency }}
                    onDeliveryMethodChange={handleDeliveryMethodChange}
                    disabled={false}
                  />
                )}

                {/* Show selected delivery summary */}
                {deliveryMethod === 'CHOWDECK' && chowdeckDeliveryFee !== null && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#fff7ed',
                    borderRadius: '8px',
                    border: '1px solid #fed7aa'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '13px', color: '#c2410c' }}>
                        ⚡ Chowdeck Express selected
                      </span>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#ea580c' }}>
                        {formatCurrency(chowdeckDeliveryFee)}
                      </span>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleUpdateDelivery}
                  disabled={deliveryLoading || deliveryZonesLoading || !shippingInfo.state || !shippingInfo.address1 || deliveryZones.length === 0}
                  style={{
                    width: '100%',
                    padding: '12px',
                    marginTop: '16px',
                    background: (deliveryLoading || deliveryZonesLoading || !shippingInfo.state || !shippingInfo.address1 || deliveryZones.length === 0) ? '#9ca3af' : '#f59e0b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: (deliveryLoading || deliveryZonesLoading || !shippingInfo.state || !shippingInfo.address1 || deliveryZones.length === 0) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {deliveryLoading ? 'Updating...' : deliveryZonesLoading ? 'Loading...' :
                   deliveryMethod === 'CHOWDECK' ? 'Continue with Chowdeck Express' : 'Calculate Delivery & Continue'}
                </button>
              </div>
            )}

            {/* Warning Banner - Show when delivery is needed but form not open yet */}
            {!isPaid && needsDeliveryAddress && !showDeliveryForm && (
              <div className="no-print" style={{
                background: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '8px',
                padding: '16px 20px',
                marginBottom: '16px',
                textAlign: 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '8px' }}>
                  <svg width="24" height="24" fill="none" stroke="#92400e" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span style={{ color: '#92400e', fontSize: '15px', fontWeight: '600' }}>
                    Delivery Address Required
                  </span>
                </div>
                <p style={{ color: '#78350f', fontSize: '13px', margin: '0 0 12px' }}>
                  Please provide your delivery address to calculate shipping and proceed with payment.
                </p>
                <button
                  onClick={() => {
                    setShowDeliveryForm(true);
                    // Fetch delivery zones if not already loaded
                    if (deliveryZones.length === 0 && !deliveryZonesLoading) {
                      setDeliveryZonesLoading(true);
                      axios.get(`/invoices/${orderId}/delivery-zones?token=${token}`)
                        .then(res => {
                          if (res.data.success) {
                            console.log('[Invoice] Fetched delivery zones:', res.data.deliveryZones);
                            setDeliveryZones(res.data.deliveryZones);
                          }
                        })
                        .catch(err => console.error('Failed to fetch delivery zones:', err))
                        .finally(() => setDeliveryZonesLoading(false));
                    }
                  }}
                  style={{
                    background: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Add Delivery Address
                </button>
              </div>
            )}

            {/* Payment Section - Only show when not paid AND delivery is set (or not needed) */}
            {!isPaid && !needsDeliveryAddress && invoice.enabledGateways?.length > 0 && (
              <div className="no-print" style={{
                background: '#f9fafb',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '16px'
              }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1e3a5f', margin: '0 0 16px' }}>
                  Select Payment Method
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {invoice.enabledGateways.map((gateway) => (
                    <label
                      key={gateway.name}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '14px 16px',
                        border: selectedGateway === gateway.name ? '2px solid #1e3a5f' : '1px solid #e5e7eb',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: selectedGateway === gateway.name ? '#f0f4f8' : 'white',
                        transition: 'all 0.2s'
                      }}
                    >
                      <input
                        type="radio"
                        name="gateway"
                        value={gateway.name}
                        checked={selectedGateway === gateway.name}
                        onChange={(e) => setSelectedGateway(e.target.value)}
                        style={{ display: 'none' }}
                      />
                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: selectedGateway === gateway.name ? '6px solid #1e3a5f' : '2px solid #d1d5db',
                        transition: 'all 0.2s'
                      }} />
                      {gateway.logo && (
                        <img
                          src={gateway.logo}
                          alt={gateway.displayName}
                          style={{ height: '24px', objectFit: 'contain' }}
                        />
                      )}
                      <span style={{ fontWeight: '500', color: '#111827', fontSize: '14px' }}>
                        {gateway.displayName || gateway.name}
                      </span>
                    </label>
                  ))}
                </div>

                {/* Email Input - Show if email is required or not present */}
                {(requiresEmail || !invoice.customer?.email) && (
                  <div style={{ marginTop: '16px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                      Email Address {requiresEmail && <span style={{ color: '#ef4444' }}>*</span>}
                    </label>
                    <input
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="Enter your email for payment receipt"
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#1e3a5f'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Your payment receipt will be sent to this email
                    </p>
                  </div>
                )}

                <button
                  onClick={handlePayment}
                  disabled={paymentLoading || (requiresEmail && !customerEmail)}
                  style={{
                    width: '100%',
                    padding: '14px',
                    marginTop: '20px',
                    background: (paymentLoading || (requiresEmail && !customerEmail)) ? '#9ca3af' : '#1e3a5f',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: (paymentLoading || (requiresEmail && !customerEmail)) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {paymentLoading ? 'Processing...' : `Pay ${formatCurrency(invoice.total)}`}
                </button>
              </div>
            )}

            {/* Bank Transfer Details */}
            {!isPaid && invoice.bankDetails && (
              <div style={{
                background: '#eff6ff',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#1e3a5f', margin: '0 0 12px' }}>
                  Bank Transfer Details
                </h4>
                <div style={{ fontSize: '13px', color: '#1e3a5f' }}>
                  <p style={{ margin: '4px 0' }}><strong>Bank:</strong> {invoice.bankDetails.bankName}</p>
                  <p style={{ margin: '4px 0' }}><strong>Account Name:</strong> {invoice.bankDetails.accountName}</p>
                  <p style={{ margin: '4px 0' }}><strong>Account Number:</strong> {invoice.bankDetails.accountNumber}</p>
                </div>
              </div>
            )}

            {/* Paid Status Message */}
            {isPaid && (
              <div style={{
                background: '#dcfce7',
                borderRadius: '8px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: '#22c55e',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px'
                }}>
                  <svg width="24" height="24" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#166534', margin: '0 0 4px' }}>
                  Payment Received
                </h3>
                <p style={{ fontSize: '13px', color: '#15803d', margin: 0 }}>
                  Paid on {formatDate(invoice.paidAt)}
                </p>
                {invoice.paymentReference && (
                  <p style={{ fontSize: '12px', color: '#16a34a', margin: '8px 0 0' }}>
                    Reference: {invoice.paymentReference}
                  </p>
                )}
              </div>
            )}

            {/* Notes */}
            {invoice.notes && (
              <div style={{
                marginTop: '16px',
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '8px'
              }}>
                <h4 style={{ fontSize: '12px', fontWeight: '600', color: '#1e3a5f', margin: '0 0 8px', textTransform: 'uppercase' }}>
                  Notes
                </h4>
                <p style={{ fontSize: '13px', color: '#374151', margin: 0, whiteSpace: 'pre-wrap' }}>
                  {invoice.notes}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid #e5e7eb',
            background: '#f9fafb',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
              {invoice.store?.name} {invoice.store?.phone && `| ${invoice.store.phone}`} {invoice.store?.email && `| ${invoice.store.email}`}
            </p>
          </div>

          {/* Adire Pattern Bottom Border */}
          <div style={{
            height: '16px',
            background: adirePattern,
            backgroundColor: '#f0f4f8',
            borderTop: '3px solid #1e3a5f'
          }} />
        </div>

        {/* Print Button - Below the invoice card */}
        <div className="no-print" style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '24px'
        }}>
          <button
            onClick={() => window.print()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: '#1e3a5f',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 8px rgba(30, 58, 95, 0.3)'
            }}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6z" />
            </svg>
            Print Invoice
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoicePage;
